import {
  ColumnFiltersState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { Exclusion } from '../exclusions.models';
import { getExclusionsColumnDefs } from './exclusions-column-def';
import { CustomTable } from 'common/components/table/customTable';
import { useState } from 'react';
import { fuzzyFilter } from 'common/components/table/table-functions/filters';
import Button from 'common/components/buttons/button';
import { useDispatch, useSelector } from 'react-redux';
import {
  onExclusionToBeDeletedChanged,
  selectExclusionToBeDeleted
} from './exclusions-table.slice';
import { onSuccess } from 'features/toast/toast.slice';
import { ModalSimple } from 'features/modal/modal-simple';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import { useDeleteExclusionMutation } from 'app/apis/commandApi';

export interface ExclusionsTableProps {
  exclusions: Exclusion[];
  isSuperUser: boolean;
}

export const ExclusionsTable = ({
  exclusions,
  isSuperUser
}: ExclusionsTableProps) => {
  const dispatch = useDispatch();
  const exclusionToBeDeleted = useSelector(selectExclusionToBeDeleted);

  const [exclusionToBeDeletedMutation, { isLoading }] =
    useDeleteExclusionMutation();

  const onDeleteExclusionConfirmed = () => {
    const exclusionToDelete: Exclusion = {
      ...exclusionToBeDeleted!
    };

    void exclusionToBeDeletedMutation(exclusionToDelete)
      .unwrap()
      .then(() => {
        dispatch(
          onSuccess({
            title: 'Exclusion Deleted',
            description: `The Exclusion '${
              exclusionToBeDeleted!.additionalInformation
            }' has been deleted successfully.`
          })
        );

        dispatch(onExclusionToBeDeletedChanged());
      })
      .catch(() => dispatch(onExclusionToBeDeletedChanged()));
  };

  const columnDefs = getExclusionsColumnDefs(isSuperUser);

  // start filtering system
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const exclusionsTable = useReactTable({
    data: exclusions,
    columns: columnDefs,
    filterFns: {
      fuzzy: fuzzyFilter
    },
    state: {
      columnFilters
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnResizing: true,
    debugTable: false,
    debugHeaders: false,
    debugColumns: false
  });

  return (
    <>
      <Button
        classOverrides="px-2 py-1.5"
        theme={ButtonTheme.SECONDARY}
        identifier="reset-exclusions-filter"
        onClick={() => {
          setColumnFilters([]);
        }}
      >
        <div className="flex items-center justify-between">
          <i className="ra-icons-generic-filter text-sm mr-2 flex"></i>
          <span>Reset Filters</span>
        </div>
      </Button>
      <ModalSimple
        isOpen={exclusionToBeDeleted !== undefined}
        theme={ButtonTheme.ERROR}
        title="Delete Exclusion"
        isLoading={isLoading}
        description={`Are you sure you want to delete exclusion '${exclusionToBeDeleted?.additionalInformation}'`}
        confirmButtonText="Delete Exclusion"
        onConfirmModal={() => onDeleteExclusionConfirmed()}
        onCancelModal={() => dispatch(onExclusionToBeDeletedChanged())}
      />

      <CustomTable
        key={`exclusions-table`}
        componentId={'exclusions-table'}
        table={exclusionsTable}
        cellClasses=""
      />
    </>
  );
};

export default ExclusionsTable;
